import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';

export function SignInRoute() {
  const { t } = useTranslation();
  const { signInWithGoogle, configured, redirectError, user } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If a previous redirect round-trip surfaced an error, show it. Refresh
  // the toggle whenever the context updates so a fresh attempt starts clean.
  useEffect(() => {
    if (redirectError) setError(redirectError.message);
  }, [redirectError]);

  // If the user is already signed in (returned from a successful redirect),
  // bounce them home so they don't see the sign-in card briefly.
  useEffect(() => {
    if (user) nav('/', { replace: true });
  }, [user, nav]);

  const onGoogle = async () => {
    setPending(true);
    setError(null);
    try {
      // Redirect mode never returns here on the happy path — the browser
      // navigates away to accounts.google.com mid-call.
      await signInWithGoogle();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.error'));
      setPending(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-[420px] flex-col gap-4 px-4 py-12">
      <Link to="/" className="text-center text-sm text-ink-soft hover:underline">
        ← {t('auth.skip')}
      </Link>
      <div
        className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-8 text-center shadow-s2"
      >
        <div className="brand-mark-big mx-auto mb-4" aria-hidden="true">
          O
        </div>
        <h1 className="m-0 mb-2 text-2xl font-bold">{t('auth.title')}</h1>
        <p className="m-0 mb-4 text-sm leading-relaxed text-ink-soft">{t('auth.body')}</p>

        {!configured && (
          <p className="m-0 mb-3 rounded-md border border-[color:var(--warn)] bg-[color:var(--surface)] p-2 text-xs text-ink-soft">
            Firebase is not configured in this environment. Sign-in is disabled.
          </p>
        )}

        <button
          type="button"
          onClick={onGoogle}
          disabled={!configured || pending}
          className="flex min-h-[52px] w-full items-center justify-center gap-3 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 text-base font-semibold text-ink transition hover:-translate-y-0.5 hover:border-soi-400 hover:shadow-s1 disabled:opacity-60"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.45.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
          </svg>
          {pending ? '…' : t('auth.google')}
        </button>

        {error && <p className="mt-3 text-xs text-[color:var(--danger)]">{error}</p>}

        <p className="mt-4 text-[11px] leading-relaxed text-ink-muted">
          By continuing you agree to our{' '}
          <a href="/privacy" className="underline">
            Privacy
          </a>{' '}
          policy.
        </p>
      </div>
      <Link
        to="/"
        className="block w-full rounded-md py-3 text-center text-sm font-semibold text-ink-soft hover:bg-soi-100"
      >
        {t('auth.skip')}
      </Link>
      <style>
        {`
          .brand-mark-big {
            position: relative;
            display: grid;
            place-items: center;
            width: 56px;
            height: 56px;
            background: var(--soi-600);
            color: #fff;
            border-radius: 10px;
            font-weight: 900;
            font-size: 28px;
            overflow: hidden;
          }
          .brand-mark-big::after {
            content: '';
            position: absolute;
            top: -10px; right: -10px;
            width: 22px; height: 22px;
            background: var(--surface);
            border-radius: 50%;
          }
        `}
      </style>
    </div>
  );
}
